import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import TableUtils from '../../../utils/tableUtils';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import DataGridComponent from '../../common/dataGrid';
import CustomStringLink from '../../common/customStringLink';
import UrlConstants from '../../../constants/UrlConstants';
import CustomLink from '../../common/customLink';
import NullableFormattedDate from '../../common/nullableFormattedDate';

interface ReportHeaders004ListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
}

const messages = {
    month: LanguageUtils.createMessage('Month'),
    id: LanguageUtils.createMessage('id'),
    logData: LanguageUtils.createMessage('Log data'),
    orderSource: LanguageUtils.createMessage('Order source'),
    serviceGroupCode: LanguageUtils.createMessage('Service group code'),
    sourceLine: LanguageUtils.createMessage('Source line'),
    notReceived: LanguageUtils.createMessage('Not received'),
    needHandling: LanguageUtils.createMessage('need handling'),
    calcelled: LanguageUtils.createMessage('cancelled'),
    ready: LanguageUtils.createMessage('ready'),
    readyNOK: LanguageUtils.createMessage('ready NOK'),
    readyEUR: LanguageUtils.createMessage('ready EUR'),
    invoiced: LanguageUtils.createMessage('invoiced'),
    invoicedNOK: LanguageUtils.createMessage('invoiced NOK'),
    invoicedEUR: LanguageUtils.createMessage('invoiced EUR')
};

const ReportHeaders004List = forwardRef((props: ReportHeaders004ListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_KICKBACK_POSTEN);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();

        return [
            {
                field: 'departureMonth',
                headerName: intl.formatMessage(messages.month),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.departureMonth}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('departureMonth', hiddenColumns)
            },
            {
                field: 'logData',
                headerName: `${intl.formatMessage(messages.logData)}`,
                flex: 0.5,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <CustomLink link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(params).id}`}>
                        <NullableFormattedDate value={params.row?.logData} />
                    </CustomLink>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('serviceDate', hiddenColumns)
            },
            {
                field: 'orderSource',
                headerName: intl.formatMessage(messages.orderSource),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.orderSource}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('orderSource', hiddenColumns)
            },
            {
                field: 'serviceGroupCode',
                headerName: intl.formatMessage(messages.serviceGroupCode),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.serviceGroupCode}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('serviceGroupCode', hiddenColumns)
            },
            {
                field: 'sourceLine',
                headerName: intl.formatMessage(messages.sourceLine),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.sourceLine}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('sourceLine', hiddenColumns)
            },
            {
                field: 'notRecieved',
                headerName: intl.formatMessage(messages.notReceived),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.notRecieved}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('notRecieved', hiddenColumns)
            },
            {
                field: 'needHandling',
                headerName: intl.formatMessage(messages.needHandling),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.needHandling}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('needHandling', hiddenColumns)
            },
            {
                field: 'calcelled',
                headerName: intl.formatMessage(messages.calcelled),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.calcelled}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('calcelled', hiddenColumns)
            },
            {
                field: 'ready',
                headerName: intl.formatMessage(messages.ready),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.ready}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('ready', hiddenColumns)
            },
            {
                field: 'readyNOK',
                headerName: intl.formatMessage(messages.readyNOK),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.readyNOK}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('readyNOK', hiddenColumns)
            },
            {
                field: 'readyEUR',
                headerName: intl.formatMessage(messages.readyEUR),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.readyEUR}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('readyEUR', hiddenColumns)
            },
            {
                field: 'invoiced',
                headerName: intl.formatMessage(messages.invoiced),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoiced}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoiced', hiddenColumns)
            },
            {
                field: 'invoicedNOK',
                headerName: intl.formatMessage(messages.invoicedNOK),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoicedNOK}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoicedNOK', hiddenColumns)
            },
            {
                field: 'invoicedEUR',
                headerName: intl.formatMessage(messages.invoicedEUR),
                flex: 0.5,
                align: 'right',
                headerAlign: 'right',
                sortable: true,
                renderCell: (param: GridRenderCellParams) => {
                    return <CustomStringLink align={'right'} value={applyRowFn(param)?.invoicedEUR}
                        link={`/#/${UrlConstants.REPORT_HEADER_004}/${applyRowFn(param)?.id}`} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('invoicedEUR', hiddenColumns)
            }
        ];
    }

    return (
        <DataGridComponent getRowId={(row) => row?.amount + Math.random()} rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default ReportHeaders004List;
